import { useCallback } from 'react'
import { Button } from 'blaze-ui/Button'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { Upload5 } from 'blaze-ui/icons/20'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import {
  UPLOAD_AUDIO_MIME_TYPES,
  UPLOAD_DOCUMENT_MIME_TYPES,
  UPLOAD_IMAGE_MIME_TYPES,
  UPLOAD_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { useModals } from 'almanac-ui/Modal'
import { useCreateMediaObjectsWithProgress } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { useCreateSource } from 'hooks/queries/workspaces/useCreateSource'
import { UploadCampaignSourceModal } from './UploadCampaignSourceModal/UploadCampaignSourceModal'
import { AnchorType, Source } from 'types'

const DEFAULT_ACCEPT = [...UPLOAD_DOCUMENT_MIME_TYPES, ...UPLOAD_AUDIO_MIME_TYPES, ...UPLOAD_VIDEO_MIME_TYPES].join(',')
const ACCEPT_WITH_IMAGES = [
  ...UPLOAD_DOCUMENT_MIME_TYPES,
  ...UPLOAD_AUDIO_MIME_TYPES,
  ...UPLOAD_VIDEO_MIME_TYPES,
  ...UPLOAD_IMAGE_MIME_TYPES,
].join(',')

export interface UploadButtonProps extends Omit<ButtonProps, 'onPress'> {
  onSourceCreated: (source: Source) => Promise<any> | void
  acceptImages?: boolean
  useToastLoading?: boolean
  onUploadStart?: () => void
  onUploadEnd?: () => void
}

export const UploadButton = ({
  variant = 'secondary',
  onSourceCreated,
  acceptImages = false,
  useToastLoading = false,
  onUploadStart,
  onUploadEnd,
  children = ' Upload File',
  ...buttonProps
}: UploadButtonProps) => {
  const { openModal } = useModals()
  const { mutateAsync: createMediaObjects } = useCreateMediaObjectsWithProgress({ onProgress: () => {} })
  const { mutateAsync: createSource } = useCreateSource()

  const handleFileToast = useCallback(
    async (file: File) => {
      onUploadStart?.()
      const dismiss = showToastMessage('Uploading file...', 'loading', undefined, 0)
      try {
        const mediaObjects = await createMediaObjects([{ file, filename: file.name }])
        const result = await createSource({ media_object_id: mediaObjects[0].id, type: 'SourceMaterialsMediaObject' })
        await onSourceCreated(result.source)
        dismiss()
        showToastMessage('File uploaded', 'success', undefined, 3)
      } catch {
        dismiss()
        showToastMessage('There was a problem uploading your file. Try again.', 'error', undefined, 5)
      } finally {
        onUploadEnd?.()
      }
    },
    [createMediaObjects, createSource, onSourceCreated, onUploadStart, onUploadEnd],
  )

  return (
    <FileTrigger
      onSelect={(files) => {
        if (!files) return

        if (files[0].size > 1024 * 1024 * 1024) {
          showErrorMessageToast(
            'Your file exceeds our 1 GB limit. Please upload a smaller file, or compress your current one.',
          )
          return
        }

        if (useToastLoading) {
          handleFileToast(files[0])
          return
        }

        let anchorType: AnchorType = 'document'
        if (files[0].type.includes('audio')) {
          anchorType = 'audio'
        } else if (files[0].type.includes('video')) {
          anchorType = 'video'
        }

        openModal(UploadCampaignSourceModal, { file: files[0], anchorType, onSourceCreated }, { newStack: true })
      }}
      accept={acceptImages ? ACCEPT_WITH_IMAGES : DEFAULT_ACCEPT}
      allowsMultiple={false}
      aria-label="Upload Campaign Source"
    >
      <Button variant={variant} frontIcon={Upload5} {...buttonProps}>
        {children}
      </Button>
    </FileTrigger>
  )
}
